import React, { useEffect, useMemo, useRef, useState } from 'react';
import './MessageReactions.css';

export const REACTION_MAP = {
  like: { emoji: '👍', label: 'Like' },
  heart: { emoji: '❤️', label: 'Heart' },
  // laugh: { emoji: "😂", label: "Laugh" },
  // wow: { emoji: "😮", label: "Wow" },
  // sad: { emoji: "😢", label: "Sad" },
  // angry: { emoji: "😡", label: "Angry" },
};

function normalizeReactionType(v) {
  return String(v || '')
    .trim()
    .toLowerCase();
}

function isActiveReaction(reaction) {
  if (reaction?.is_deleted === undefined && reaction?.is_active === undefined) return true;
  const isDeleted = reaction?.is_deleted === true;
  const isActive = reaction?.is_active !== false;
  return !isDeleted && isActive;
}

function safeUuid() {
  try {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  } catch {}
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function getOrCreateLocalReactorId() {
  try {
    const key = 'sure_chat_reactor_id';
    const existing = localStorage.getItem(key);
    if (existing) return existing;
    const created = safeUuid();
    localStorage.setItem(key, created);
    return created;
  } catch {
    return safeUuid();
  }
}

export default function MessageReactions({
  reactions = [],
  allowedReactions = [],
  currentUserNativeId = null,
  currentReactorType = 'user',
  onSetReaction,
  onDeleteReaction,
}) {
  const [local, setLocal] = useState(Array.isArray(reactions) ? reactions : []);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [poppingType, setPoppingType] = useState(null);
  const [flipDown, setFlipDown] = useState(false);
  const wrapRef = useRef(null);
  const hoverbarRef = useRef(null);

  useEffect(() => {
    setLocal(Array.isArray(reactions) ? reactions : []);
  }, [reactions]);

  useEffect(() => {
    const onDoc = (e) => {
      if (!pickerOpen) return;
      if (!wrapRef.current) return;
      if (!wrapRef.current.contains(e.target)) setPickerOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [pickerOpen]);

  // Auto-flip the hoverbar below the bubble if it would clip the top of the
  // chat-messages scroll container.
  useEffect(() => {
    const bar = hoverbarRef.current;
    if (!bar) return undefined;
    const bubble = bar.closest('.message-bubble');
    if (!bubble) return undefined;

    const measure = () => {
      const bubbleRect = bubble.getBoundingClientRect();
      const scrollContainer =
        bubble.closest('.chat-messages') || bubble.closest('.messages-container');
      const containerTop = scrollContainer ? scrollContainer.getBoundingClientRect().top : 0;
      const headroom = bubbleRect.top - containerTop;
      setFlipDown(headroom < 60);
    };

    bubble.addEventListener('mouseenter', measure);
    return () => bubble.removeEventListener('mouseenter', measure);
  }, []);

  const myId = useMemo(() => {
    const v = currentUserNativeId ? String(currentUserNativeId) : '';
    return v || getOrCreateLocalReactorId();
  }, [currentUserNativeId]);

  const activeReactions = useMemo(
    () => (Array.isArray(local) ? local.filter(isActiveReaction) : []),
    [local],
  );

  const allowedList = useMemo(() => {
    const base =
      Array.isArray(allowedReactions) && allowedReactions.length > 0
        ? allowedReactions
        : Object.keys(REACTION_MAP);

    const out = [];
    const seen = new Set();

    for (const v of base) {
      const t = normalizeReactionType(v);
      if (!REACTION_MAP[t]) continue;
      if (seen.has(t)) continue;
      seen.add(t);
      out.push(t);
    }
    return out;
  }, [allowedReactions]);

  const allowedSet = useMemo(() => new Set(allowedList), [allowedList]);

  const quickReactions = useMemo(() => allowedList.slice(0, 3), [allowedList]);
  const showKebab = allowedList.length > 3;
  const menuReactions = useMemo(() => allowedList, [allowedList]);

  const grouped = useMemo(() => {
    const byType = new Map();
    for (const reaction of activeReactions) {
      const reactionType = normalizeReactionType(reaction?.reaction);
      if (!REACTION_MAP[reactionType]) continue;
      const arr = byType.get(reactionType) || [];
      arr.push(reaction);
      byType.set(reactionType, arr);
    }

    return Array.from(byType.entries())
      .map(([type, items]) => ({
        type,
        items,
        count: items.length,
        mine: items.some((x) => String(x?.reactor_native_id) === myId),
      }))
      .filter((g) => allowedSet.has(g.type));
  }, [activeReactions, myId, allowedSet]);

  const toggleReaction = async (type) => {
    const reactionType = normalizeReactionType(type);
    if (!REACTION_MAP[reactionType]) return;
    if (!allowedSet.has(reactionType)) return;

    const hadMine = activeReactions.some(
      (reaction) =>
        normalizeReactionType(reaction?.reaction) === reactionType &&
        String(reaction?.reactor_native_id) === myId,
    );

    const prev = local;

    try {
      if (hadMine) {
        setLocal(
          prev.map((reaction) => {
            if (
              isActiveReaction(reaction) &&
              normalizeReactionType(reaction?.reaction) === reactionType &&
              String(reaction?.reactor_native_id) === myId
            ) {
              return {
                ...reaction,
                is_active: false,
                is_deleted: true,
                updated_at: new Date().toISOString(),
              };
            }
            return reaction;
          }),
        );
        setPickerOpen(false);

        if (typeof onDeleteReaction === 'function') {
          await onDeleteReaction(reactionType);
        }
        return;
      }

      setLocal([
        ...prev,
        {
          key: safeUuid(),
          reaction: reactionType,
          reaction_type: currentReactorType,
          reactor_native_id: myId,
          updated_at: new Date().toISOString(),
          is_active: true,
          is_deleted: false,
        },
      ]);
      setPickerOpen(false);
      setPoppingType(reactionType);

      if (typeof onSetReaction === 'function') {
        await onSetReaction(reactionType);
      }
    } catch (e) {
      setLocal(prev);
      console.error('Reaction API failed:', e);
    }
  };

  const buildTooltip = (group) => {
    const label = REACTION_MAP[group.type]?.label || '';
    if (group.count === 1) {
      return group.mine ? `You reacted with ${label}` : label;
    }
    if (group.mine) {
      const others = group.count - 1;
      return others === 1 ? `You and 1 other` : `You and ${others} others`;
    }
    return `${group.count} ${label}`;
  };

  return (
    <>
      <div
        ref={(el) => {
          wrapRef.current = el;
          hoverbarRef.current = el;
        }}
        className={`reaction-hoverbar ${flipDown ? 'flip-down' : ''} ${pickerOpen ? 'is-open' : ''}`}
      >
        {quickReactions.map((t) => (
          <button
            key={t}
            type="button"
            className="reaction-quick"
            onClick={() => toggleReaction(t)}
            title={REACTION_MAP[t]?.label}
            aria-label={REACTION_MAP[t]?.label}
          >
            <span className="reaction-emoji">{REACTION_MAP[t]?.emoji}</span>
          </button>
        ))}

        {showKebab && (
          <button
            type="button"
            className={`reaction-kebab-btn ${pickerOpen ? 'open' : ''}`}
            onClick={() => setPickerOpen((v) => !v)}
            title="More"
            aria-label="More"
          >
            <span className="reaction-kebab" aria-hidden="true">
              ⋮
            </span>
          </button>
        )}

        {pickerOpen && showKebab && (
          <div className="reaction-picker" role="menu">
            {menuReactions.map((t) => (
              <button
                key={t}
                type="button"
                className="reaction-pick"
                onClick={() => toggleReaction(t)}
                title={REACTION_MAP[t]?.label}
                aria-label={REACTION_MAP[t]?.label}
              >
                <span className="reaction-emoji">{REACTION_MAP[t]?.emoji}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {grouped.length > 0 && (
        <div className="message-reactions">
          {grouped.map((group) => {
            const isPopping = poppingType === group.type;
            return (
              <button
                key={group.type}
                type="button"
                className={`reaction-chip ${group.mine ? 'active' : ''} ${isPopping ? 'reaction-chip--pop' : ''}`}
                onClick={() => toggleReaction(group.type)}
                onAnimationEnd={() => {
                  if (isPopping) setPoppingType(null);
                }}
              >
                <span className="reaction-emoji">{REACTION_MAP[group.type].emoji}</span>
                <span className="reaction-count">{group.count}</span>
                <span className="reaction-tooltip" role="tooltip">
                  {buildTooltip(group)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </>
  );
}
